/**
 * Assessment Health Stage — Stage 6b: Macro-governance over the assessment pool.
 *
 * ARCHITECTURE v4.0 — S8 / FG-AssessmentPool (mei 2026)
 *
 * Companion to `diversityStage` (batch-only, family-grouped) but at a
 * different scale: where `diversityStage` asks "is THIS family's type-mix
 * varied?", this stage asks "is our MC pool per (module, tier-band)
 * actually large enough to assemble a Cito-style toets?".
 *
 * Layer A — CAPACITY:
 *   Group MC-eligible questions by (module, tierBand). Emit a coach when
 *   the slice is below `MINIMUM_VIABLE_POOL_SIZE`, an info when it's in
 *   the "thin" band, nothing when healthy. Status: 'gap' | 'thin' | 'ok'.
 *
 * Layer B — DISTRIBUTION (this commit):
 *   For slices that aren't a `gap`, also check whether the MC-eligible
 *   pool spreads across cito-difficulty buckets and covers enough distinct
 *   DOK levels. Both emit under the rollup code
 *   `ASSESSMENT_POOL_DISTRIBUTION` with `metadata.reason ∈
 *   { 'cito_concentration', 'dok_coverage' }` — same pattern as
 *   `CONTENT_CITO_ISSUE` and `SUPPORT_EXPLANATION_QUALITY`.
 *
 * Layer C (feasibility: assessmentBlueprintGenerator dry-run) is planned
 * for a later sprint — code ASSESSMENT_POOL_FEASIBILITY is reserved.
 *
 * Design rules (anti-bandaid):
 *  - #2 status is an enum, not a boolean — `'gap' | 'thin' | 'ok'`.
 *  - #4 default kind is coach/info, NEVER blocker. Pool-health is
 *       diagnostic; it never overrules a per-question gate verdict.
 *  - DDD boundary: this stage does NOT import from `assessment/`. It
 *       re-uses `isAssessmentEligibleType` from `policyEngine` (which is
 *       the same predicate `assessment/utils/assessmentEligibility.ts`
 *       consumes), plus an inline 4-line structural MC check. One
 *       eligibility definition, two consumers — no drift.
 *
 * @see docs/audits/ARCHITECTURAL_PEER_REVIEW_PLAN_2026.md (§S8)
 */

import type { ValidationIssue } from '../issueContract'
import { coach, info } from '../issueContract'
import type { Question, ModuleId } from '../../../content'
import { STAGES } from '../stageDefinitions'
import { isAssessmentEligibleType, getTierBand } from '../policyEngine'
import type { TierBand } from '../../tierBands'
import {
  resolveMinimumViableSize,
  classifyPoolStatus,
  COMFORT_MULTIPLIER,
  bucketCitoDifficulty,
  classifyCitoConcentration,
  resolveMinimumDokCoverage,
  CITO_BUCKET_COUNT,
  CITO_CONCENTRATION_MIN_SAMPLE,
  CITO_CONCENTRATION_HARD_THRESHOLD,
  CITO_CONCENTRATION_SOFT_THRESHOLD,
  type AssessmentPoolStatus,
} from '../assessmentHealthThresholds'
import {
  listAssessmentCombos,
  simulateBlueprintFeasibility,
  tierToBlueprintBand,
  BLUEPRINT_DEFAULT_QUESTION_COUNT,
  BLUEPRINT_BANDS,
  type BlueprintBand,
  type FamilyAvailability,
  type FeasibilitySimulationResult,
} from '../assessmentBlueprintSpec'
import { getFamiliesForModule } from '../../../content/taxonomy/taxonomyHelpers'
import { getAllowedTiersForGroup } from '../../../content/taxonomy/tiers'
import type { GroupId } from '../../../config/systemConfig'

const STAGE = STAGES.ASSESSMENT_HEALTH

// ─── Local MC-Eligibility Check ──────────────────────────────────────────────

/**
 * Inline structural MC-eligibility check.
 *
 * Why duplicate this instead of importing `isAssessmentEligible` from
 * `src/assessment/utils/assessmentEligibility.ts`? DDD boundary —
 * `governance/` is upstream of `assessment/`, and crossing that line via
 * `assessment/` would re-introduce the eager-import problem that the
 * existing assessmentEligibility module documents in its header.
 *
 * The shared predicate (`isAssessmentEligibleType` — the type-policy
 * decision) IS imported from `policyEngine`, which is the SSOT for both
 * consumers. Only the structural shape-check is local.
 */
function isMcEligible(q: Question): boolean {
  const t = q.questionType ?? 'multiple_choice'
  if (!isAssessmentEligibleType(t)) return false
  if (t !== 'multiple_choice') return false
  if (!q.options || q.options.length !== 4) return false
  if (typeof q.correctAnswer !== 'number') return false
  if (q.correctAnswer < 0 || q.correctAnswer >= q.options.length) return false
  return true
}

// ─── Module Resolver ─────────────────────────────────────────────────────────

/**
 * Resolve the module a question belongs to. We deliberately do not call into
 * `applyEnforcementModes.deriveModuleFromFamily` here — that helper is
 * concerned with enforcement-mode lookup (string codes for the config store),
 * while we need a `ModuleId` that matches the seed `module` field on Question.
 * Honour the seed's own `module` field first; fall back to UNKNOWN.
 */
function resolveModule(q: Question): ModuleId | 'UNKNOWN' {
  return (q.module as ModuleId | undefined) ?? 'UNKNOWN'
}

// ─── Batch Stage Entry Point ─────────────────────────────────────────────────

export function runAssessmentHealthBatch(
  questions: Question[],
): ValidationIssue[] {
  if (questions.length === 0) return []

  // Slice key: `${module}::${tierBand}`
  type SliceKey = string
  interface Slice {
    module: ModuleId | 'UNKNOWN'
    band: TierBand
    mcEligible: Question[]
    representative: Question
  }
  const slices = new Map<SliceKey, Slice>()

  for (const q of questions) {
    const tier = typeof q.tier === 'number' ? q.tier : 0
    if (tier <= 0) continue // Untiered questions are not part of any pool
    const band = getTierBand(tier) as TierBand
    const module = resolveModule(q)
    if (module === 'UNKNOWN') continue // Cannot attribute to a module-pool
    const key: SliceKey = `${module}::${band}`

    const existing = slices.get(key)
    if (existing) {
      if (isMcEligible(q)) existing.mcEligible.push(q)
    } else {
      slices.set(key, {
        module,
        band,
        mcEligible: isMcEligible(q) ? [q] : [],
        representative: q,
      })
    }
  }

  const issues: ValidationIssue[] = []
  for (const slice of slices.values()) {
    const capacityStatus = resolveCapacityStatus(
      slice.module,
      slice.band,
      slice,
    )
    const capacityIssue = buildSliceIssue(slice.module, slice.band, slice)
    if (capacityIssue) issues.push(capacityIssue)

    // Layer B — only run distribution checks on slices that have enough
    // questions to be analysed. A 'gap' slice already gets a louder Layer A
    // signal; piling distribution findings on top would be noise.
    if (capacityStatus !== 'gap') {
      issues.push(...buildDistributionIssues(slice.module, slice.band, slice))
    }
  }

  // Layer C — blueprint-feasibility dry-run per (group, module).
  issues.push(...runFeasibilityChecks(questions))

  return issues
}

/**
 * Extract the capacity status (gap/thin/ok) without rebuilding the issue.
 * Used to decide whether Layer B should run on a given slice.
 */
function resolveCapacityStatus(
  module: ModuleId,
  band: TierBand,
  slice: { mcEligible: Question[] },
): AssessmentPoolStatus {
  const minimumViableSize = resolveMinimumViableSize(module, band)
  return classifyPoolStatus(slice.mcEligible.length, minimumViableSize)
}

// ─── Per-Slice Issue Builder ─────────────────────────────────────────────────

function buildSliceIssue(
  module: ModuleId,
  band: TierBand,
  slice: { mcEligible: Question[]; representative: Question },
): ValidationIssue | null {
  const mcEligibleCount = slice.mcEligible.length
  const minimumViableSize = resolveMinimumViableSize(module, band)
  const comfortSize = Math.ceil(minimumViableSize * COMFORT_MULTIPLIER)
  const status: AssessmentPoolStatus = classifyPoolStatus(
    mcEligibleCount,
    minimumViableSize,
  )

  if (status === 'ok') return null

  const representative = slice.representative
  const qId = representative.id ?? 'UNKNOWN'
  const familyId = representative.familyId ?? 'UNKNOWN'

  const remediationNeeded = Math.max(0, minimumViableSize - mcEligibleCount)
  const metadata: Record<string, unknown> = {
    module,
    tierBand: band,
    mcEligibleCount,
    minimumViableSize,
    comfortSize,
    status,
    remediation: { needed: remediationNeeded },
  }

  if (status === 'gap') {
    // Action-oriented message — prescriptive, not descriptive.
    const message =
      `${module} / ${band}: pool heeft ${mcEligibleCount} MC-vragen, ` +
      `minimum is ${minimumViableSize}. Voeg ${remediationNeeded}+ ` +
      `MC-eligible vragen toe om een Cito-toets uit deze tier-band te kunnen samenstellen.`

    return coach('assessmentPool', STAGE, 'ASSESSMENT_POOL_CAPACITY', message, {
      questionId: qId,
      familyId,
      field: 'pool.capacity',
      expected: `≥ ${minimumViableSize} MC-eligible vragen in ${module}/${band}`,
      actual: `${mcEligibleCount} MC-eligible vragen`,
      suggestion: `Voeg ${remediationNeeded}+ MC-vragen toe in ${module}/${band} (tier-band: ${band}).`,
      severity: 35,
      metadata,
    })
  }

  // status === 'thin' — info-level, visible but not urgent
  const headroom = comfortSize - mcEligibleCount
  const message =
    `${module} / ${band}: pool werkt (${mcEligibleCount} MC-vragen, ` +
    `minimum ${minimumViableSize}) maar zit onder de comfort-grens ${comfortSize}. ` +
    `Voeg ${headroom}+ vragen toe om herhaling tussen sessies te vermijden.`

  return info('assessmentPool', STAGE, 'ASSESSMENT_POOL_CAPACITY', message, {
    questionId: qId,
    familyId,
    field: 'pool.capacity',
    suggestion: `Streef naar ≥ ${comfortSize} MC-vragen in ${module}/${band} voor sessie-variatie.`,
    severity: 10,
    metadata,
  })
}

// ─── Layer B — Distribution Checks ───────────────────────────────────────────

/**
 * Per-slice distribution analysis. Emits 0..2 issues:
 *
 *  - 'cito_concentration' — one cito-difficulty bucket dominates the pool.
 *    Emitted as `coach` when share ≥ HARD threshold, `info` when ≥ SOFT.
 *    Silently skipped when fewer than CITO_CONCENTRATION_MIN_SAMPLE questions
 *    carry a citoDifficulty value (signal too noisy).
 *
 *  - 'dok_coverage' — too few distinct DOK levels represented.
 *    Emitted as `coach` because a missing DOK level makes the slice unable to
 *    fill its blueprint slot at all (not just less varied).
 *
 * Both findings emit under the rollup code ASSESSMENT_POOL_DISTRIBUTION with
 * a `metadata.reason` discriminator — matches CONTENT_CITO_ISSUE,
 * SUPPORT_EXPLANATION_QUALITY, DIVERSITY_TYPE_OVERUSE, etc.
 */
function buildDistributionIssues(
  module: ModuleId,
  band: TierBand,
  slice: { mcEligible: Question[]; representative: Question },
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const citoIssue = buildCitoConcentrationIssue(module, band, slice)
  if (citoIssue) issues.push(citoIssue)

  const dokIssue = buildDokCoverageIssue(module, band, slice)
  if (dokIssue) issues.push(dokIssue)

  return issues
}

function buildCitoConcentrationIssue(
  module: ModuleId,
  band: TierBand,
  slice: { mcEligible: Question[]; representative: Question },
): ValidationIssue | null {
  // Bucket only questions that carry a citoDifficulty value in-range.
  const bucketCounts = new Array<number>(CITO_BUCKET_COUNT).fill(0)
  let sampled = 0
  for (const q of slice.mcEligible) {
    const v = q.citoDifficulty
    if (typeof v !== 'number') continue
    const idx = bucketCitoDifficulty(v)
    if (idx === null) continue
    bucketCounts[idx]++
    sampled++
  }

  if (sampled < CITO_CONCENTRATION_MIN_SAMPLE) return null

  const { status, topBucket, topShare } = classifyCitoConcentration(
    bucketCounts,
    sampled,
  )
  if (status === 'ok') return null

  const representative = slice.representative
  const qId = representative.id ?? 'UNKNOWN'
  const familyId = representative.familyId ?? 'UNKNOWN'
  const sharePct = Math.round(topShare * 100)
  const bucketLabel = `bucket ${topBucket + 1}/${CITO_BUCKET_COUNT}`

  const metadata: Record<string, unknown> = {
    module,
    tierBand: band,
    reason: 'cito_concentration',
    bucketCounts,
    sampled,
    topBucket,
    topShare,
    softThreshold: CITO_CONCENTRATION_SOFT_THRESHOLD,
    hardThreshold: CITO_CONCENTRATION_HARD_THRESHOLD,
  }

  if (status === 'dominated') {
    const message =
      `${module} / ${band}: ${sharePct}% van de cito-getagde MC-vragen zit in ${bucketLabel} ` +
      `(drempel ${Math.round(CITO_CONCENTRATION_HARD_THRESHOLD * 100)}%). ` +
      `Spreid vragen over meer difficulty-buckets om een Cito-blueprint betrouwbaar te kunnen samenstellen.`
    return coach(
      'assessmentPool',
      STAGE,
      'ASSESSMENT_POOL_DISTRIBUTION',
      message,
      {
        questionId: qId,
        familyId,
        field: 'pool.distribution.cito',
        expected: `Geen bucket > ${Math.round(CITO_CONCENTRATION_HARD_THRESHOLD * 100)}% van de pool`,
        actual: `${sharePct}% in ${bucketLabel}`,
        suggestion: `Voeg MC-vragen toe in de andere difficulty-buckets binnen ${module}/${band}.`,
        severity: 25,
        metadata,
      },
    )
  }

  // status === 'lopsided' — info level
  const message =
    `${module} / ${band}: ${sharePct}% van de cito-getagde MC-vragen zit in ${bucketLabel}. ` +
    `Pool is bruikbaar maar lopsided — meer spreiding helpt blueprint-stabiliteit.`
  return info(
    'assessmentPool',
    STAGE,
    'ASSESSMENT_POOL_DISTRIBUTION',
    message,
    {
      questionId: qId,
      familyId,
      field: 'pool.distribution.cito',
      suggestion: `Streef naar een gelijkmatigere verdeling over de ${CITO_BUCKET_COUNT} cito-buckets binnen ${module}/${band}.`,
      severity: 10,
      metadata,
    },
  )
}

function buildDokCoverageIssue(
  module: ModuleId,
  band: TierBand,
  slice: { mcEligible: Question[]; representative: Question },
): ValidationIssue | null {
  // Collect distinct DOK levels actually present in the MC-eligible pool.
  const levelsPresent = new Set<number>()
  let dokTagged = 0
  for (const q of slice.mcEligible) {
    const dok = q.pedagogy?.dok
    if (typeof dok !== 'number') continue
    dokTagged++
    levelsPresent.add(dok)
  }

  // If almost nothing is DOK-tagged we can't draw a coverage conclusion —
  // SEED_DOK_MISSING already shouts about that on each question.
  if (dokTagged < CITO_CONCENTRATION_MIN_SAMPLE) return null

  const distinctCount = levelsPresent.size
  const required = resolveMinimumDokCoverage(band)
  if (distinctCount >= required) return null

  const representative = slice.representative
  const qId = representative.id ?? 'UNKNOWN'
  const familyId = representative.familyId ?? 'UNKNOWN'

  const presentSorted = Array.from(levelsPresent).sort((a, b) => a - b)
  const missingHint =
    required === 2
      ? '(DOK 1 + DOK 2 minimum)'
      : '(DOK 1 + DOK 2 + DOK 3 minimum)'

  const metadata: Record<string, unknown> = {
    module,
    tierBand: band,
    reason: 'dok_coverage',
    levelsPresent: presentSorted,
    distinctCount,
    required,
    dokTagged,
  }

  const message =
    `${module} / ${band}: pool dekt slechts ${distinctCount} DOK-niveau${distinctCount === 1 ? '' : "'s"} ` +
    `(aanwezig: ${presentSorted.length ? presentSorted.join(', ') : '–'}). Voor ${band} is ${required} ${missingHint} vereist. ` +
    `Een Cito-blueprint kan deze slice niet vullen zonder bredere DOK-dekking.`

  return coach(
    'assessmentPool',
    STAGE,
    'ASSESSMENT_POOL_DISTRIBUTION',
    message,
    {
      questionId: qId,
      familyId,
      field: 'pool.distribution.dok',
      expected: `≥ ${required} distinct DOK levels in ${module}/${band}`,
      actual: `${distinctCount} (${presentSorted.join(', ') || 'geen'})`,
      suggestion: `Voeg MC-vragen toe met de ontbrekende DOK-niveaus in ${module}/${band}.`,
      severity: 30,
      metadata,
    },
  )
}

// ─── Layer C — Blueprint Feasibility (per group × module) ────────────────────

/**
 * Run the blueprint-feasibility simulator for every (group, module) combo
 * and emit ASSESSMENT_POOL_FEASIBILITY issues for non-'ok' outcomes.
 *
 * Operates on the SAME `questions[]` universe that Layers A and B saw, so
 * the three layers share one worldview — no drift between "what the pool
 * says" and "what the blueprint can".
 */
function runFeasibilityChecks(questions: Question[]): ValidationIssue[] {
  // Pre-bucket MC-eligible questions by familyId once, so the per-combo
  // inner loop is O(combos × families) rather than O(combos × questions).
  const mcByFamily = new Map<string, Question[]>()
  for (const q of questions) {
    if (!isMcEligible(q)) continue
    const familyId = q.familyId
    if (!familyId) continue
    const list = mcByFamily.get(familyId)
    if (list) list.push(q)
    else mcByFamily.set(familyId, [q])
  }

  const issues: ValidationIssue[] = []
  for (const { group, module } of listAssessmentCombos()) {
    const allowedTiers = getAllowedTiersForGroup(group)
    const families = getFamiliesForModule(module)

    const availabilities: FamilyAvailability[] = []
    for (const fam of families) {
      const familyQuestions = mcByFamily.get(fam.familyId) ?? []
      if (familyQuestions.length === 0) continue
      const byBand: Record<BlueprintBand, number> = { low: 0, mid: 0, high: 0 }
      let representativeQuestionId: string | null = null
      let total = 0
      for (const q of familyQuestions) {
        const tier = typeof q.tier === 'number' ? q.tier : 0
        if (tier <= 0) continue
        const band = tierToBlueprintBand(tier, allowedTiers)
        if (!band) continue
        byBand[band]++
        total++
        if (!representativeQuestionId && q.id) {
          representativeQuestionId = q.id
        }
      }
      if (total === 0) continue
      availabilities.push({
        familyId: fam.familyId,
        module,
        mcEligibleByBand: byBand,
        totalMcEligible: total,
        representativeQuestionId,
      })
    }

    const result = simulateBlueprintFeasibility(availabilities)
    if (result.status === 'ok') continue

    const issue = buildFeasibilityIssue(group, module, result)
    if (issue) issues.push(issue)
  }
  return issues
}

function buildFeasibilityIssue(
  group: GroupId,
  module: ModuleId,
  result: FeasibilitySimulationResult,
): ValidationIssue | null {
  if (result.status === 'ok') return null

  const qId = result.representativeQuestionId ?? 'BATCH'
  const baseMetadata: Record<string, unknown> = {
    group,
    module,
    reason: result.status,
    achievable: result.achievable,
    target: result.target,
    bandAchievable: result.bandAchievable,
    bandTarget: result.bandTarget,
    bandFamilies: result.bandFamilies,
    emptyBands: result.emptyBands,
  }

  if (result.status === 'zero_questions') {
    const message =
      `Groep ${group} / ${module}: 0 MC-vragen beschikbaar voor blueprint. ` +
      `Voeg ${result.target}+ MC-eligible vragen toe binnen het tier-bereik van groep ${group}.`
    return coach(
      'assessmentPool',
      STAGE,
      'ASSESSMENT_POOL_FEASIBILITY',
      message,
      {
        questionId: qId,
        familyId: 'UNKNOWN',
        field: 'pool.feasibility',
        expected: `≥ ${result.target} MC-vragen voor Groep ${group} / ${module}`,
        actual: `0 MC-vragen`,
        suggestion: `Voeg MC-eligible vragen toe voor Groep ${group} in ${module} (over low/mid/high tier-bands).`,
        severity: 50,
        metadata: baseMetadata,
      },
    )
  }

  if (result.status === 'shortfall') {
    const gap = result.target - result.achievable
    const message =
      `Groep ${group} / ${module}: blueprint-generator kan slechts ${result.achievable}/${result.target} ` +
      `vragen plaatsen (low ${result.bandAchievable.low}/${result.bandTarget.low} · ` +
      `mid ${result.bandAchievable.mid}/${result.bandTarget.mid} · ` +
      `high ${result.bandAchievable.high}/${result.bandTarget.high}). ` +
      `Een volledige Cito-toets kan niet worden samengesteld.`
    return coach(
      'assessmentPool',
      STAGE,
      'ASSESSMENT_POOL_FEASIBILITY',
      message,
      {
        questionId: qId,
        familyId: 'UNKNOWN',
        field: 'pool.feasibility',
        expected: `${result.target} bereikbare MC-vragen`,
        actual: `${result.achievable} bereikbaar`,
        suggestion: `Voeg ${gap}+ MC-vragen toe in Groep ${group} / ${module}, gespreid over de low/mid/high tier-bands.`,
        severity: 40,
        metadata: baseMetadata,
      },
    )
  }

  // status === 'degraded' — hit total target but one or more bands incomplete
  const bandSummary = BLUEPRINT_BANDS.map(
    (b) => `${b} ${result.bandAchievable[b]}/${result.bandTarget[b]}`,
  ).join(' · ')
  const emptyHint =
    result.emptyBands.length > 0
      ? ` Lege banden: ${result.emptyBands.join(', ')}.`
      : ''
  const message =
    `Groep ${group} / ${module}: blueprint-target gehaald (${result.achievable}/${result.target}) ` +
    `maar tier-band-verdeling is onvolledig (${bandSummary}).${emptyHint} ` +
    `Toets blijft samenstelbaar maar minder representatief voor het curriculum.`
  return info('assessmentPool', STAGE, 'ASSESSMENT_POOL_FEASIBILITY', message, {
    questionId: qId,
    familyId: 'UNKNOWN',
    field: 'pool.feasibility',
    suggestion: `Voeg MC-vragen toe in de zwakste tier-band(en) van Groep ${group} / ${module}.`,
    severity: 20,
    metadata: baseMetadata,
  })
}
